import {createJSONStorage, persist} from "zustand/middleware";
import {AuthButtonVO, AuthRouteVO, useQueryAuthButtons, useQueryAuthRoutes} from "@/service/menu.s";
import {create} from "zustand/react";

enum MenuEnum {
    AuthRoutes = "authRoutes",
    AuthButton = "authButton",
    Breadcrumbs = "breadcrumbs",
}

export interface BreadcrumbType {
    path: string;
    name: string;
}

export interface AuthButtonType {
    [code: string]: AuthButtonVO[];
}

type MenuStore = {
    authRoutes: AuthRouteVO[];
    authButton: AuthButtonType;
    breadcrumbs: BreadcrumbType[];
    actions: {
        setAuthRoutes: (authRoutes: AuthRouteVO[]) => void;
        setAuthButton: (authButton: AuthButtonType) => void;
        setBreadcrumbs: (payload: BreadcrumbType[]) => void;
    };
};

const useMenuStore = create<MenuStore>()(
    persist(
        (set) => ({
            authButton: {},
            authRoutes: [],
            breadcrumbs: [],
            actions: {
                setAuthRoutes: (authRoutes: AuthRouteVO[]) => set({authRoutes}),
                setAuthButton: (authButton: AuthButtonType) => set({authButton}),
                setBreadcrumbs: (payload: BreadcrumbType[]) =>
                    set({breadcrumbs: payload}),
            },
        }),
        {
            name: "menuStore",
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                [MenuEnum.AuthRoutes]: state.authRoutes,
                [MenuEnum.AuthButton]: state.authButton,
                [MenuEnum.Breadcrumbs]: state.breadcrumbs,
            }),
        },
    ),
);

export const useGetAuthMenu = () => {
    const {setAuthRoutes, setAuthButton} = useMenuActions();
    const {mutateAsync: queryAuthRoutesMutate} = useQueryAuthRoutes();
    const {mutateAsync: queryAuthButtonsMutate} = useQueryAuthButtons();
    const formatButtons = (buttons: AuthButtonVO[]): AuthButtonType => {
        return buttons?.reduce((prev: AuthButtonType, next: AuthButtonVO) => {
            if (next.children) {
                return {...prev, [next.code]: next.children};
            } else {
                return prev;
            }
        }, {} as AuthButtonType)
    };
    const authMenus = async () => {
        const {data: routes} = await queryAuthRoutesMutate();
        const {data: buttons} = await queryAuthButtonsMutate();
        setAuthRoutes(routes!);
        setAuthButton(formatButtons(buttons!));
    };
    return authMenus;
};

export const useGetAuthButtons = (code: string, actions: number[]) => {
    const authButton = useAuthButton();
    return authButton[code]?.filter((item) => actions?.indexOf(item.action) > -1);
};

export const useAuthButton = () => useMenuStore((state) => state.authButton);
export const useAuthRoutes = () => useMenuStore((state) => state.authRoutes);
export const useBreadcrumbs = () => useMenuStore((state) => state.breadcrumbs);

export const useMenuActions = () => useMenuStore((state) => state.actions);
